import React from 'react';

import { CheckCircleFilled, UserOutlined } from '@ant-design/icons';
import { Avatar, Empty, List, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import classNames from 'classnames';

import { EMPTY_MESSAGES } from '@/shared/lib/constants';
import type { OwnerEditHook } from '@/shared/types/editHooks';
import { Button } from '@/shared/ui/Button';
import { Icon } from '@/shared/ui/Icon';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import styles from './OwnerEditPopover.module.css';

export type OwnerEditPopoverProps = {
  ownerEdit: OwnerEditHook;
};

export const OwnerEditPopover: React.FC<OwnerEditPopoverProps> = ({ ownerEdit }) => {
  const {
    activeTab,
    teamOwners,
    userOwners,
    selectedTeamId,
    selectedUserIds,
    currentOwners,
    isLoadingTeams,
    isLoadingUsers,
    isUpdating,
    hasChanges,
    setActiveTab,
    handleTeamSelect,
    handleUserSelect,
    handleUpdate,
    handleClose,
  } = ownerEdit;
  const tabItems: TabsProps['items'] = [
    {
      key: 'teams',
      label: `팀 ${teamOwners.length}`,
      children: (
        <div className={styles.listContainer}>
          {isLoadingTeams ? (
            <LoadingState message="" size={32} />
          ) : teamOwners.length > 0 ? (
            <List
              dataSource={teamOwners}
              renderItem={(team) => {
                const isSelected = selectedTeamId === team.id;
                const isExisting = currentOwners.some((owner) => owner.id === team.id && owner.type === 'team');
                return (
                  <List.Item
                    onClick={() => handleTeamSelect(team.id)}
                    className={classNames('listItem', styles.listItem, {
                      selected: isSelected && !isExisting,
                      existing: isSelected && isExisting,
                    })}>
                    <div className={styles.listItemContent}>
                      <Icon name="users" size={16} />
                      <span>{team.displayName}</span>
                    </div>
                  </List.Item>
                );
              }}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_MESSAGES.NO_TEAMS} />
          )}
        </div>
      ),
    },
    {
      key: 'users',
      label: `사용자 ${userOwners.length}`,
      children: (
        <div className={styles.listContainer}>
          {isLoadingUsers ? (
            <LoadingState message="" size={32} />
          ) : userOwners.length > 0 ? (
            <List
              dataSource={userOwners}
              renderItem={(user) => {
                const isSelected = selectedUserIds.includes(user.id);
                const isExisting = currentOwners.some((owner) => owner.id === user.id && owner.type === 'user');
                return (
                  <List.Item
                    onClick={() => handleUserSelect(user.id, !isSelected)}
                    className={classNames('listItem', styles.listItem, {
                      selected: isSelected && !isExisting,
                      existing: isSelected && isExisting,
                    })}>
                    <div className={styles.listItemContent}>
                      <Avatar icon={<UserOutlined />} size={24} />
                      <span>{user.displayName || user.name}</span>
                    </div>
                    {isSelected && <CheckCircleFilled className={styles.checkIcon} />}
                  </List.Item>
                );
              }}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_MESSAGES.NO_USERS} />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.popover}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="small" />
      <div className={styles.actions}>
        <div className={styles.buttonGroup}>
          <Button variant="filled" color="standard" onClick={handleUpdate} loading={isUpdating} disabled={!hasChanges}>
            업데이트
          </Button>
          <Button variant="outlined" color="standard" onClick={handleClose}>
            취소
          </Button>
        </div>
      </div>
    </div>
  );
};
